/**
 * The web's own sign-in to a Home Planner account: the site's `/auth/web-token` hands a 30-day token back to
 * `/auth/done` on this origin, the web keeps it in this browser, renews it quietly near expiry and forgets it on
 * sign-out. The site takes no state parameter, so a return counts only while this browser's own «Войти» marker is
 * fresh: a token planted from another site is ignored.
 */
type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** What the module reads from the page; tests hand in their own. */
export interface WebAuthEnv {
  storage: StorageLike | null;
  now: () => number;
  /** This page's origin, which the site sends the token back to. */
  origin: string;
  /** Running inside a frame: the quiet renewal's own frame never renews. */
  framed: boolean;
  /** Where another tab's `storage` events arrive: the page's `window`. */
  window: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null;
}

/** The return from the site: signed in or failed, with the address to go back to; or one this browser never asked for. */
export type SignInReturn = { kind: 'signed-in' | 'failed'; back: string | null } | { kind: 'ignored' };

interface WebAuthRecord { token: string; expires: number; triedAt?: number }
interface PendingSignIn { at: number; back: string | null }

const RECORD_KEY = 'hp.webAuth';
const PENDING_KEY = 'hp.webAuth.pending';
const RENEW_KEY = 'hp.webAuth.renewal';
/** The site's host, the same site as the web, where its sign-in cookie lives; never the catalog's. */
const SITE_ORIGIN = 'https://homeplannerapp.com';
const MINUTE = 60_000, DAY = 24 * 60 * MINUTE;
/** Longer than the site's 10-minute e-mail sign-in link, which may finish in another tab. */
const PENDING_TTL = 30 * MINUTE;
const RENEW_WINDOW = 7 * DAY;
const RENEW_GAP = DAY;
const RENEW_TIMEOUT = 20_000;
/** `setTimeout` fires at once above this delay (24.8 days), shorter than a 30-day sign-in. */
const MAX_TIMER = 2 ** 31 - 1;
const TOKEN = /^[\w-]+\.[\w-]+\.[\w-]+$/;

let custom: WebAuthEnv | null = null;
let page: WebAuthEnv | null = null;
/** The record as this page knows it, also when storage refuses it; undefined until read. */
let record: WebAuthRecord | null | undefined;
let notice: 'failed' | null = null;
const listeners = new Set<() => void>();
let listening: WebAuthEnv['window'] = null;
let expiryTimer: ReturnType<typeof setTimeout> | undefined;
/** Takes the quiet renewal's frame away and stops its timer; null when none is in flight. */
let endRenewal: (() => void) | null = null;

/** Tests hand in their own env, null goes back to the page's; either way the module forgets what it remembered. */
export function useWebAuthEnv(env: WebAuthEnv | null): void {
  listening?.removeEventListener('storage', onStorage);
  listening = null;
  clearTimeout(expiryTimer);
  expiryTimer = undefined;
  endRenewal?.();
  listeners.clear();
  record = undefined;
  notice = null;
  custom = env;
}

function env(): WebAuthEnv {
  const current = custom ?? (page ??= pageEnv());
  // For the page's whole life, not only while home subscribes: the editor too must hear another tab and its own renewal.
  if (!listening && current.window) {
    current.window.addEventListener('storage', onStorage);
    listening = current.window;
  }
  return current;
}

function pageEnv(): WebAuthEnv {
  const browser = typeof window !== 'undefined';
  let storage: StorageLike | null = null;
  try { storage = typeof localStorage === 'undefined' ? null : localStorage; } catch { /* blocked site data: signed out */ }
  return { storage, now: Date.now, origin: browser ? location.origin : '', framed: browser && window.parent !== window, window: browser ? window : null };
}

function readJson(key: string): unknown {
  try {
    const raw = env().storage?.getItem(key);
    return raw ? JSON.parse(raw) as unknown : null;
  } catch { return null; }
}

function writeJson(key: string, value: unknown): void {
  try { env().storage?.setItem(key, JSON.stringify(value)); } catch { /* full or blocked: this page keeps it in memory */ }
}

function forget(key: string): void {
  try { env().storage?.removeItem(key); } catch { /* blocked site data */ }
}

function parseRecord(value: unknown): WebAuthRecord | null {
  const { token, expires, triedAt } = (value ?? {}) as Partial<Record<keyof WebAuthRecord, unknown>>;
  if (typeof token !== 'string' || !TOKEN.test(token) || typeof expires !== 'number' || !Number.isFinite(expires)) return null;
  return typeof triedAt === 'number' && Number.isFinite(triedAt) ? { token, expires, triedAt } : { token, expires };
}

function readRecord(): WebAuthRecord | null {
  if (record === undefined) record = parseRecord(readJson(RECORD_KEY));
  if (record && record.expires <= env().now()) {
    // Storage may hold a newer sign-in this page did not hear about: only a dead one is deleted.
    const stored = parseRecord(readJson(RECORD_KEY));
    record = stored && stored.expires > env().now() ? stored : null;
    if (!record) forget(RECORD_KEY);
  }
  return record;
}

/** The live token for a request's `Authorization` header, or null when signed out; never for an address or a log. */
export function webAuthToken(): string | null {
  return readRecord()?.token ?? null;
}

function keep(next: WebAuthRecord | null): void {
  record = next;
  if (next) writeJson(RECORD_KEY, next);
  else forget(RECORD_KEY);
}

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/** Subscribers hear the expiry too: home offers «Войти» again the moment the token dies. */
function armExpiry(): void {
  clearTimeout(expiryTimer);
  expiryTimer = undefined;
  const expires = listeners.size ? readRecord()?.expires : undefined;
  if (expires === undefined) return;
  expiryTimer = setTimeout(() => { armExpiry(); notify(); }, Math.min(Math.max(0, expires - env().now()), MAX_TIMER));
}

function onStorage(event: Event): void {
  const { key } = event as StorageEvent;
  if (key !== RECORD_KEY && key !== null) return;
  record = undefined;
  armExpiry();
  notify();
}

/** A path on this site; anything that could leave it (`//host`, `/\host`, a full address) is dropped. */
function sameSitePath(back: unknown): string | null {
  return typeof back === 'string' && /^\/(?![/\\])/.test(back) ? back : null;
}

export function signInUrl(origin: string): string {
  return `${SITE_ORIGIN}/auth/web-token?returnUrl=${encodeURIComponent(`${origin}/auth/done`)}`;
}

export function startSignIn(back: string | null): void {
  const { now, origin } = env();
  writeJson(PENDING_KEY, { at: now(), back } satisfies PendingSignIn);
  location.assign(signInUrl(origin));
}

export function takeSignInReturn(hash: string): SignInReturn {
  // The renewal's frame answers only the renewal's marker and a page only «Войти»'s, so neither uses up the other's.
  const key = env().framed ? RENEW_KEY : PENDING_KEY;
  const pending = readJson(key) as Partial<PendingSignIn> | null;
  if (!pending) return { kind: 'ignored' };
  forget(key);
  const now = env().now();
  if (typeof pending.at !== 'number' || !(now - pending.at <= PENDING_TTL)) return { kind: 'ignored' };
  const back = sameSitePath(pending.back);
  const fields = new URLSearchParams(hash.replace(/^#/, ''));
  const token = fields.get('token') ?? '', expires = Date.parse(fields.get('expires') ?? '');
  if (!TOKEN.test(token) || !(expires > now)) {
    notice = 'failed';
    return { kind: 'failed', back };
  }
  keep({ token, expires });
  notify();
  return { kind: 'signed-in', back };
}

/** The stored sign-in's expiry in ms, or null when signed out: a primitive, for `useSyncExternalStore`. */
export function webAuthSnapshot(): number | null {
  return readRecord()?.expires ?? null;
}

export function subscribeWebAuth(onChange: () => void): () => void {
  listeners.add(onChange);
  armExpiry();
  return () => {
    listeners.delete(onChange);
    if (!listeners.size) armExpiry();
  };
}

/**
 * Forgets the web's token in this browser; the site's own sign-in stays, so the next «Войти» needs no form. A renewal
 * in flight, this tab's or another's, ends with it: its frame would otherwise bring a fresh token back.
 */
export function signOut(): void {
  endRenewal?.();
  forget(RENEW_KEY);
  keep(null);
  armExpiry();
  notify();
}

export function renewalDue(): boolean {
  const { framed, now } = env();
  const kept = framed ? null : readRecord();
  if (!kept || kept.expires - now() > RENEW_WINDOW) return false;
  return kept.triedAt === undefined || now() - kept.triedAt >= RENEW_GAP;
}

/** Once the page is idle, asks the site for a fresh token in a hidden frame; nothing shows, and a failure waits for tomorrow. */
export function scheduleRenewal(): void {
  if (!renewalDue() || typeof document === 'undefined') return;
  if (typeof requestIdleCallback === 'function') requestIdleCallback(renewQuietly, { timeout: 5000 });
  else setTimeout(renewQuietly, 1000);
}

function renewQuietly(): void {
  // Read afresh: another tab may have signed out or renewed since this page started.
  record = undefined;
  const kept = readRecord();
  if (!kept || !renewalDue()) return;
  const { now, origin, window: target } = env();
  const at = now();
  keep({ ...kept, triedAt: at });
  writeJson(RENEW_KEY, { at } satisfies Pick<PendingSignIn, 'at'>);
  const frame = document.createElement('iframe');
  frame.hidden = true;
  frame.tabIndex = -1;
  frame.setAttribute('aria-hidden', 'true');
  // No allow-top-navigation: a login page that busts frames cannot take the editor away.
  frame.setAttribute('sandbox', 'allow-scripts allow-same-origin');
  frame.src = signInUrl(origin);
  const end = () => {
    endRenewal = null;
    clearTimeout(timer);
    target?.removeEventListener('storage', onRenewed);
    frame.remove();
  };
  const finish = () => {
    end();
    if ((readJson(RENEW_KEY) as Partial<PendingSignIn> | null)?.at === at) forget(RENEW_KEY);
  };
  const onRenewed = (event: Event) => { if ((event as StorageEvent).key === RECORD_KEY) finish(); };
  const timer = setTimeout(finish, RENEW_TIMEOUT);
  target?.addEventListener('storage', onRenewed);
  endRenewal = end;
  document.body.append(frame);
}

/** The failure of the return this page was opened by, once: home shows it as a notice. */
export function takeSignInNotice(): 'failed' | null {
  const value = notice;
  notice = null;
  return value;
}
